import { Badge } from "@/components/ui/badge";

type ChangelogTag = "feature" | "launch" | "fix" | "improvement";

interface ChangelogEntry {
  date: string;
  version: string;
  title: string;
  description: string;
  tags: ChangelogTag[];
  items: string[];
}

const TAG_VARIANTS: Record<
  ChangelogTag,
  "default" | "destructive" | "secondary"
> = {
  feature: "default",
  launch: "default",
  fix: "destructive",
  improvement: "secondary",
};

const TAG_CLASS: Record<ChangelogTag, string> = {
  feature: "bg-brand/15 text-brand border-brand/25",
  launch: "bg-brand/15 text-brand border-brand/25",
  fix: "",
  improvement: "",
};

const entries: ChangelogEntry[] = [
  {
    date: "March 2026",
    version: "v0.2.0",
    title: "Asset Manager",
    description:
      "Upload, organize, and reuse your files directly inside Rayloop. Images and documents are now first-class citizens — pick from your library anywhere in a pipeline instead of re-uploading every time.",
    tags: ["feature"],
    items: [
      "Upload images, files, and documents to your personal asset library",
      "In-pipeline asset picker — browse and select from any node's settings panel",
      "Attach files directly in the AI chat to reference them in your workflow",
      "Type-aware filtering — image pickers show only images, file pickers show everything",
    ],
  },
  {
    date: "February 2026",
    version: "v0.1.0",
    title: "Beta Launch",
    description:
      "The first public version of Rayloop. Build node-based AI pipelines on a visual canvas, connect generation steps with typed edges, and run them end-to-end with real AI providers.",
    tags: ["launch"],
    items: [
      "Visual drag-and-drop canvas with 27 node types across input, generation, transform, and output",
      "Multi-provider AI: Google Gemini, Anthropic Claude, and OpenAI in one pipeline",
      "Parallel pipeline execution — nodes at the same level run concurrently",
      "Live run status streamed over WebSocket as each node completes",
      "Three-panel layout: AI chat assistant, canvas, and node settings inspector",
      "One-click Google sign-in",
    ],
  },
];

export function ChangelogTimeline() {
  return (
    <section className="mx-auto max-w-4xl px-6 pb-24">
      {entries.map((entry, idx) => (
        <div
          key={entry.version}
          className="relative flex flex-col md:flex-row md:gap-12"
        >
          {/* Date column */}
          <div className="mb-2 shrink-0 md:mb-0 md:w-48 md:sticky md:top-24 md:self-start md:text-right">
            <time className="text-muted-foreground text-sm">{entry.date}</time>
            {/* <div className="mt-1">
              <Badge variant="outline" className="text-xs font-mono">
                {entry.version}
              </Badge>
            </div> */}
          </div>

          {/* Timeline line + dot */}
          <div className="relative hidden md:flex flex-col items-center">
            <div className="size-3 rounded-full bg-brand shrink-0 mt-1.5" />
            {idx < entries.length - 1 && (
              <div className="w-px flex-1 border-l-2 border-border" />
            )}
          </div>

          {/* Content */}
          <div className="relative pb-12 md:pb-16 flex-1">
            {/* Mobile timeline dot */}
            <div className="absolute -left-[1.125rem] top-1.5 size-3 rounded-full bg-brand md:hidden" />
            <div className="border-l-2 border-border pl-6 md:border-l-0 md:pl-0">
              <h3 className="font-display text-foreground text-xl font-normal tracking-tight sm:text-2xl">
                {entry.title}
              </h3>

              <div className="mt-2 flex flex-wrap gap-1.5">
                {entry.tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={TAG_VARIANTS[tag]}
                    className={TAG_CLASS[tag]}
                  >
                    {tag.charAt(0).toUpperCase() + tag.slice(1)}
                  </Badge>
                ))}
              </div>

              <p className="text-muted-foreground mt-3 text-sm leading-relaxed sm:text-base">
                {entry.description}
              </p>

              <ul className="mt-4 space-y-2">
                {entry.items.map((item) => (
                  <li
                    key={item}
                    className="text-foreground/80 flex items-start gap-2 text-sm"
                  >
                    <span className="text-brand mt-1.5 block size-1.5 shrink-0 rounded-full bg-current" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </section>
  );
}
